// ResetPasswordPage.js
// Same split layout, palette and fields as the Sign In page — see AuthShell.
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, TextField, Button, Alert, CircularProgress,
    Link as MuiLink, useTheme,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import axiosInstance from '../../utils/axiosInstance';
import AuthShell, {
    colors, fontFamily, makeInputSx, makePrimaryButtonSx, alertSx,
} from './AuthShell';

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPasswordPage() {
    const theme = useTheme();
    const { token } = useParams();
    const navigate = useNavigate();

    const [newPassword, setNewPassword] = useState('');
    const [verifyPassword, setVerifyPassword] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    // Checked against the backend before the form is shown, so an expired link
    // says so straight away instead of after the user types a new password.
    const [linkState, setLinkState] = useState({ checking: true, valid: false, reason: '' });

    useEffect(() => {
        document.title = 'Set New Password | KlonIT Web Application';
    }, []);

    useEffect(() => {
        let cancelled = false;

        const checkLink = async () => {
            try {
                const { data } = await axiosInstance.get(`/reset-password/${token}/validate`);
                if (!cancelled) {
                    setLinkState({ checking: false, valid: Boolean(data.valid), reason: data.message || '' });
                }
            } catch (err) {
                if (!cancelled) {
                    setLinkState({
                        checking: false,
                        valid: false,
                        reason: err?.response?.data?.message
                            || 'This password reset link is not valid. Request a new one.',
                    });
                }
            }
        };

        checkLink();
        return () => { cancelled = true; };
    }, [token]);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage('');
        setError('');

        if (newPassword !== verifyPassword) {
            setError('Passwords do not match.');
            return;
        }
        if (newPassword.length < MIN_PASSWORD_LENGTH) {
            setError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters long.`);
            return;
        }

        setLoading(true);
        try {
            const { data } = await axiosInstance.post(`/reset-password/${token}`, {
                newPassword,
                verifyPassword,
            });
            setMessage(data.message);
            setTimeout(() => navigate('/Login'), 1500);
        } catch (err) {
            setError(err?.response?.data?.message || 'Network error. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const subtitle = linkState.valid
        ? 'Choose a new password for your account'
        : 'Reset links last 30 minutes and work once';

    return (
        <AuthShell
            icon={<LockOutlinedIcon sx={{ color: '#fff', fontSize: 24 }} />}
            title="Set New Password"
            subtitle={subtitle}
            footer={
                <>
                    <Box sx={{
                        display: 'inline-flex', alignItems: 'center', gap: 0.5,
                        fontSize: '0.6875rem', color: colors.textTertiary, fontFamily,
                    }}>
                        <ShieldOutlinedIcon sx={{ fontSize: 13, color: colors.success }} />
                        Secure
                    </Box>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.textTertiary }} />
                    <MuiLink href="/Login" underline="hover"
                             sx={{ fontSize: '0.8125rem', color: theme.palette.primary.main, fontWeight: 600, fontFamily }}>
                        Back to Sign In
                    </MuiLink>
                </>
            }
        >
            {linkState.checking && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 3 }}>
                    <CircularProgress size={18} />
                    <Typography sx={{ fontSize: '0.875rem', color: colors.textSecondary, fontFamily }}>
                        Checking your link…
                    </Typography>
                </Box>
            )}

            {!linkState.checking && !linkState.valid && (
                <Box sx={{
                    p: 2.5, borderRadius: '16px', bgcolor: 'rgba(220, 38, 38, 0.07)',
                    border: '1px solid rgba(220, 38, 38, 0.25)',
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: '10px',
                            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <ErrorOutlineOutlinedIcon sx={{ color: '#fff', fontSize: 20 }} />
                        </Box>
                        <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: colors.textPrimary, fontFamily }}>
                            This link no longer works
                        </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.8125rem', color: colors.textSecondary, mb: 2, fontFamily }}>
                        {linkState.reason}
                    </Typography>
                    <Button href="/forgot-password" fullWidth sx={makePrimaryButtonSx(theme)}>
                        Send a new link
                    </Button>
                </Box>
            )}

            {!linkState.checking && linkState.valid && (
                <form onSubmit={handleSubmit}>
                    {error && <Alert severity="error" sx={alertSx}>{error}</Alert>}
                    {message && <Alert severity="success" sx={alertSx}>{message}</Alert>}

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                        <TextField
                            fullWidth size="small" type="password" placeholder="New password"
                            value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                            required disabled={loading} autoFocus
                            helperText={`At least ${MIN_PASSWORD_LENGTH} characters`}
                            InputProps={{
                                startAdornment: <LockOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} />,
                            }}
                            sx={makeInputSx(theme)}
                        />
                        <TextField
                            fullWidth size="small" type="password" placeholder="Confirm new password"
                            value={verifyPassword} onChange={(e) => setVerifyPassword(e.target.value)}
                            required disabled={loading}
                            InputProps={{
                                startAdornment: <VpnKeyOutlinedIcon sx={{ fontSize: 18, color: colors.textTertiary, mr: 0.75 }} />,
                            }}
                            sx={makeInputSx(theme)}
                        />
                    </Box>

                    <Button type="submit" fullWidth disabled={loading}
                            sx={{ ...makePrimaryButtonSx(theme), mt: 1.75 }}>
                        {loading && <CircularProgress size={18} sx={{ color: '#fff' }} />}
                        {loading ? 'Changing…' : 'Change password'}
                    </Button>

                    <Typography sx={{
                        fontSize: '0.6875rem', color: colors.textTertiary, fontFamily,
                        textAlign: 'center', mt: 1.5,
                    }}>
                        Changing your password signs you out on every other device.
                    </Typography>
                </form>
            )}
        </AuthShell>
    );
}
